import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown, Globe2 } from 'lucide-react'
import { useLanguage } from '@/localization'
import type { LanguageCode } from '../../../shared/localization/core'

export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { language, languageOption, languages, setLanguage, t } = useLanguage()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const selectLanguage = (code: LanguageCode) => {
    setLanguage(code)
    setOpen(false)
  }

  return (
    <div
      className={`relative ${compact ? 'w-full' : ''}`}
      ref={rootRef}
      data-no-translate
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t('Select language')}
        title={t('Select language')}
        className={`group inline-flex items-center gap-2 rounded-full border bg-white/70 px-3 py-1.5 font-orbitron text-[11px] font-bold uppercase tracking-[0.14em] text-stone-700 shadow-sm backdrop-blur-md transition-all duration-200 cursor-pointer ${
          open
            ? 'border-uliunai-red/70 shadow-[0_0_14px_rgba(185,28,28,0.14)]'
            : 'border-stone-line hover:border-uliunai-red/45 hover:text-stone-900'
        } ${compact ? 'w-full justify-between' : ''}`}
      >
        <span className="inline-flex items-center gap-2">
          <Globe2
            size={14}
            className={`transition-colors ${open ? 'text-uliunai-red' : 'text-uliunai-red/90 group-hover:text-uliunai-red'}`}
            aria-hidden="true"
          />
          <span>{languageOption.label}</span>
        </span>
        <ChevronDown
          size={13}
          className={`text-stone-400 transition-transform duration-200 ${open ? 'rotate-180 text-uliunai-red' : 'group-hover:text-stone-600'}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label={t('Select language')}
          className={`absolute z-[60] mt-2 min-w-[11.5rem] overflow-hidden rounded-xl border border-stone-line bg-gradient-to-b from-white to-parchment p-1.5 shadow-2xl shadow-stone-900/15 backdrop-blur-md ${compact ? 'left-0 right-0 w-full' : 'right-0'}`}
        >
          <div className="px-2.5 pb-1.5 pt-1 font-orbitron text-[10px] font-semibold uppercase tracking-[0.22em] text-uliunai-red">
            {t('Language')}
          </div>

          <div className="space-y-0.5">
            {languages.map((item) => {
              const isActive = item.code === language

              return (
                <button
                  key={item.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => selectLanguage(item.code)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-150 cursor-pointer ${
                    isActive
                      ? 'bg-uliunai-red/10 text-stone-900 ring-1 ring-uliunai-red/30'
                      : 'text-stone-600 hover:bg-uliunai-red/5 hover:text-stone-900'
                  }`}
                >
                  <span
                    className={`inline-flex min-w-[2rem] items-center justify-center rounded-md px-1.5 py-0.5 font-orbitron text-[10px] font-bold tracking-wider ${
                      isActive
                        ? 'bg-uliunai-red/15 text-uliunai-red'
                        : 'bg-stone-100 text-stone-500'
                    }`}
                  >
                    {item.label}
                  </span>
                  <span className="flex-1 font-rajdhani text-sm font-semibold tracking-wide">
                    {item.name}
                  </span>
                  {isActive && (
                    <Check size={14} className="shrink-0 text-uliunai-red" aria-hidden="true" />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
